import { AnimaliaListItem } from '../../domain/entities/AnimaliaListItem';
import { AnimaliaListingResult } from '../../domain/entities/AnimaliaListingResult';
import {
  AnimaliaListingTaxonomy,
  AnimaliaListingTaxonomySection,
} from '../../domain/entities/AnimaliaListingTaxonomy';
import { AnimaliaNarrativeBlock } from '../../domain/entities/AnimaliaNarrativeBlock';
import { AnimaliaTaxonomyEntry } from '../../domain/entities/AnimaliaTaxonomyEntry';
import { AnimaliaTaxonomyFilterKey } from '../../domain/ports/AnimaliaListRepositoryPort';
import {
  AnimaliaNavigableTaxonomyDTO,
  TAXONOMY_LABELS,
  buildAnimaliaTaxonomyCanonicalRoute,
  createAnimaliaNavigableTaxonomy,
  isAnimaliaTaxonomyFilterKey,
} from '../taxonomy/AnimaliaNavigableTaxonomy';
import { buildRoute } from '../../../../shared/ui/router';

interface AnimaliaImageApiResponse {
  src?: string;
  alt?: string;
  imageLicense?: AnimaliaImageLicenseApiResponse;
}

interface AnimaliaImageLicenseApiResponse {
  name?: string;
  url?: string;
  artist?: string;
  credit?: string;
  attributionRequired?: boolean;
  source?: string;
}

interface AnimaliaNarrativeBlockApiResponse {
  title?: string;
  body?: string;
}

interface AnimaliaSectionApiResponse {
  title?: string;
  content?: string;
}

interface AnimaliaWikipediaApiResponse {
  title?: string;
  url?: string;
}

interface AnimaliaListingTaxonomyApiResponse {
  key?: string;
  value?: string;
  title?: string;
  summary?: string;
  description?: string;
  image?: AnimaliaImageApiResponse | string;
  imageLicense?: AnimaliaImageLicenseApiResponse;
  sections?: ReadonlyArray<AnimaliaSectionApiResponse>;
  wikipedia?: AnimaliaWikipediaApiResponse;
  wikipediaUrl?: string;
  completed?: boolean;
}

interface AnimaliaTaxonomyApiResponse {
  kingdom?: string;
  phylum?: string;
  class?: string;
  className?: string;
  order?: string;
  family?: string;
  genus?: string;
  species?: string;
}

export interface AnimaliaListItemApiResponse {
  id: string;
  completed: boolean;
  slug?: string;
  scientificName?: string;
  identificationName?: string;
  species?: string;
  commonName?: string;
  family?: string;
  continent?: string;
  country?: string;
  authority?: string;
  scientificAuthority?: string;
  taxonomicRank?: string;
  rank?: string;
  genus?: string;
  kingdom?: string;
  phylum?: string;
  class?: string;
  className?: string;
  order?: string;
  taxonomy?: AnimaliaTaxonomyApiResponse;
  image?: AnimaliaImageApiResponse | string;
  imageLicense?: AnimaliaImageLicenseApiResponse;
  narrativeBlocks?: ReadonlyArray<AnimaliaNarrativeBlockApiResponse>;
  sections?: ReadonlyArray<AnimaliaSectionApiResponse>;
  wikipedia?: AnimaliaWikipediaApiResponse;
  wikipediaUrl?: string;
}

export interface AnimaliaListResponseApiResponse {
  items: ReadonlyArray<AnimaliaListItemApiResponse>;
  taxonomy?: AnimaliaListingTaxonomyApiResponse | null;
}

export interface AnimaliaListingHeroStatDTO {
  label: string;
  value: string;
}

export interface AnimaliaListingHeroDTO {
  kicker: string;
  title: string;
  stats: ReadonlyArray<AnimaliaListingHeroStatDTO>;
}

export interface AnimaliaListingCardTagDTO {
  label: string;
  value: string;
  filterKey: AnimaliaNavigableTaxonomyDTO['filterKey'] | null;
}

export interface AnimaliaListingActiveFilterDTO extends AnimaliaNavigableTaxonomyDTO {}

export interface AnimaliaListingTaxonomySectionDTO {
  title: string;
  body: string;
}

export interface AnimaliaListingTaxonomyLandingDTO {
  key: AnimaliaNavigableTaxonomyDTO['filterKey'];
  label: string;
  value: string;
  title: string;
  summary: string | null;
  description: string | null;
  imageSrc: string | null;
  imageAlt: string;
  imageLicenseName: string | null;
  imageLicenseUrl: string | null;
  imageArtist: string | null;
  imageCredit: string | null;
  imageAttributionRequired: boolean;
  imageSource: string | null;
  wikipediaUrl: string | null;
  sections: ReadonlyArray<AnimaliaListingTaxonomySectionDTO>;
  completed: boolean;
  canonicalUrl: string;
}

export interface AnimaliaListingCardDTO {
  id: string;
  slug: string;
  positionLabel: string;
  scientificName: string;
  commonName: string;
  imageSrc: string | null;
  imageAlt: string;
  imageLicenseName: string | null;
  imageLicenseUrl: string | null;
  imageArtist: string | null;
  imageCredit: string | null;
  imageAttributionRequired: boolean;
  imageSource: string | null;
  mediaBadge: string;
  detailUrl: string;
  wikipediaUrl: string | null;
  excerptTitle: string;
  excerptBody: string;
  tags: ReadonlyArray<AnimaliaListingCardTagDTO>;
}

interface AnimaliaImageLicenseDTO {
  name: string | null;
  url: string | null;
  artist: string | null;
  credit: string | null;
  attributionRequired: boolean;
  source: string | null;
}

export interface AnimaliaListingPageDTO {
  hero: AnimaliaListingHeroDTO;
  items: ReadonlyArray<AnimaliaListingCardDTO>;
  hasMore: boolean;
  activeFilters: ReadonlyArray<AnimaliaListingActiveFilterDTO>;
  taxonomyLanding: AnimaliaListingTaxonomyLandingDTO | null;
}

export class AnimaliaListingMapper {
  static fromApiResponse(response: AnimaliaListItemApiResponse): AnimaliaListItem {
    const scientificName = AnimaliaListingMapper.resolveScientificName(response);
    const image = AnimaliaListingMapper.resolveImage(response.image, response.imageLicense, scientificName);
    const wikipedia = AnimaliaListingMapper.resolveWikipedia(response);
    const narrativeBlocks = AnimaliaListingMapper.resolveNarrativeBlocks(response);
    const slug = AnimaliaListingMapper.resolveSlug(response, scientificName);
    const family = AnimaliaListingMapper.resolveText([response.family, response.taxonomy?.family]);

    return new AnimaliaListItem(
      response.id,
      slug,
      scientificName,
      response.completed,
      response.commonName?.trim() || null,
      family,
      response.continent?.trim() || null,
      response.country?.trim() || null,
      image,
      wikipedia,
      narrativeBlocks,
      AnimaliaListingMapper.resolveText([response.authority, response.scientificAuthority]),
      AnimaliaListingMapper.resolveText([response.taxonomicRank, response.rank]),
      AnimaliaListingMapper.resolveTaxonomy(response, scientificName, family),
    );
  }

  static listingResultFromApiResponse(response: AnimaliaListResponseApiResponse): AnimaliaListingResult {
    return new AnimaliaListingResult(
      response.items.map((item) => AnimaliaListingMapper.fromApiResponse(item)),
      AnimaliaListingMapper.taxonomyFromApiResponse(response.taxonomy),
    );
  }

  static toPageDTO(
    entities: ReadonlyArray<AnimaliaListItem>,
    hasMore: boolean,
    activeFilters: ReadonlyArray<AnimaliaListingActiveFilterDTO> = [],
    taxonomyLanding: AnimaliaListingTaxonomy | null = null,
    detailReturnTo: string | null = null,
  ): AnimaliaListingPageDTO {
    const familyCount = new Set(
      entities.map((entity) => entity.family).filter((value): value is string => value !== null),
    ).size;
    const genusCount = new Set(
      entities.map((entity) => entity.getGenus()).filter((value): value is string => value !== null),
    ).size;

    return {
      hero: {
        kicker: 'Insectos de Espana',
        title: 'Criaturas que parecen sacadas de otro planeta',
        stats: [
          {
            label: 'Documentos',
            value: String(entities.length),
          },
          {
            label: 'Generos',
            value: String(genusCount),
          },
          {
            label: 'Especies',
            value: String(entities.length),
          },
          {
            label: 'Familias',
            value: String(familyCount),
          },
        ],
      },
      items: AnimaliaListingMapper.toCardCollectionDTO(entities, detailReturnTo),
      hasMore,
      activeFilters: [...activeFilters],
      taxonomyLanding: AnimaliaListingMapper.toTaxonomyLandingDTO(taxonomyLanding),
    };
  }

  static toCardCollectionDTO(
    entities: ReadonlyArray<AnimaliaListItem>,
    detailReturnTo: string | null = null,
  ): ReadonlyArray<AnimaliaListingCardDTO> {
    return entities.map((entity, index) => AnimaliaListingMapper.toCardDTO(entity, index, detailReturnTo));
  }

  private static toCardDTO(
    entity: AnimaliaListItem,
    index: number,
    detailReturnTo: string | null,
  ): AnimaliaListingCardDTO {
    const primaryNarrative = entity.getPrimaryNarrativeBlock();
    const continent = entity.continent ?? 'Unknown origin';
    const detailQuery =
      detailReturnTo !== null
        ? {
            returnTo: detailReturnTo,
          }
        : undefined;

    return {
      id: entity.id,
      slug: entity.slug,
      positionLabel: `Ficha ${String(index + 1).padStart(2, '0')}`,
      scientificName: entity.scientificName,
      commonName: entity.commonName ?? 'Sin dato',
      imageSrc: entity.image?.src ?? null,
      imageAlt: entity.image?.alt ?? entity.scientificName,
      imageLicenseName: entity.image?.license?.name ?? null,
      imageLicenseUrl: entity.image?.license?.url ?? null,
      imageArtist: entity.image?.license?.artist ?? null,
      imageCredit: entity.image?.license?.credit ?? null,
      imageAttributionRequired: entity.image?.license?.attributionRequired ?? false,
      imageSource: entity.image?.license?.source ?? null,
      mediaBadge: continent,
      detailUrl: buildRoute(`/animalia/${encodeURIComponent(entity.slug)}`, detailQuery),
      wikipediaUrl: entity.wikipedia?.url ?? null,
      excerptTitle: primaryNarrative?.title ?? 'Una historia editorial aun en construccion',
      excerptBody:
        primaryNarrative?.body ??
        'Todavia no hay un bloque narrativo publico para esta ficha, pero la especie ya forma parte del listado visible.',
      tags: [
        AnimaliaListingMapper.toNavigableTag('family', createAnimaliaNavigableTaxonomy('family', entity.family), 'Sin dato'),
        AnimaliaListingMapper.toNavigableTag(
          'genus',
          createAnimaliaNavigableTaxonomy('genus', entity.getGenus()),
          'Sin dato',
        ),
          {
            label: TAXONOMY_LABELS.species,
            value: entity.getSpeciesLabel(),
            filterKey: null,
          },
        {
          label: 'Pais',
          value: entity.country ?? 'Sin dato',
          filterKey: null,
        },
      ],
    };
  }

  private static toNavigableTag(
    filterKey: AnimaliaNavigableTaxonomyDTO['filterKey'],
    taxonomy: AnimaliaNavigableTaxonomyDTO | null,
    fallbackValue: string,
  ): AnimaliaListingCardTagDTO {
    if (!taxonomy) {
      return {
        label: TAXONOMY_LABELS[filterKey],
        value: fallbackValue,
        filterKey: null,
      };
    }

    return {
      label: taxonomy.label,
      value: taxonomy.value,
      filterKey: taxonomy.filterKey,
    };
  }

  private static resolveScientificName(response: AnimaliaListItemApiResponse): string {
    const candidates = [response.scientificName, response.identificationName, response.species];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim() !== '') {
        return candidate.trim();
      }
    }

    return 'Species pending identification';
  }

  private static resolveSlug(response: AnimaliaListItemApiResponse, scientificName: string): string {
    const explicitSlug = response.slug?.trim();

    if (explicitSlug) {
      return explicitSlug;
    }

    const slugBase = scientificName || response.id;

    return slugBase
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || response.id;
  }

  private static resolveText(candidates: ReadonlyArray<string | undefined | null>): string | null {
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim() !== '') {
        return candidate.trim();
      }
    }

    return null;
  }

  private static resolveImage(
    image: AnimaliaImageApiResponse | string | undefined,
    topLevelImageLicense: AnimaliaImageLicenseApiResponse | undefined,
    scientificName: string,
  ): { src: string; alt: string; license: AnimaliaImageLicenseDTO | null } | null {
    if (typeof image === 'string' && image.trim() !== '') {
      return {
        src: image,
        alt: scientificName || 'Animalia image',
        license: AnimaliaListingMapper.resolveImageLicense(topLevelImageLicense),
      };
    }

    if (typeof image === 'object' && image !== null && image.src?.trim()) {
      return {
        src: image.src,
        alt: image.alt?.trim() || scientificName || 'Animalia image',
        license: AnimaliaListingMapper.resolveImageLicense(
          image.imageLicense ?? topLevelImageLicense,
        ),
      };
    }

    return null;
  }

  private static resolveImageLicense(
    imageLicense: AnimaliaImageLicenseApiResponse | undefined,
  ): AnimaliaImageLicenseDTO | null {
    if (typeof imageLicense !== 'object' || imageLicense === null) {
      return null;
    }

    const name = AnimaliaListingMapper.resolveText([imageLicense.name]);
    const url = AnimaliaListingMapper.resolveText([imageLicense.url]);
    const artist = AnimaliaListingMapper.resolveText([imageLicense.artist]);
    const credit = AnimaliaListingMapper.resolveText([imageLicense.credit]);
    const source = AnimaliaListingMapper.resolveText([imageLicense.source]);
    const attributionRequired = imageLicense.attributionRequired === true;

    if (!name && !url && !artist && !credit && !source && !attributionRequired) {
      return null;
    }

    return {
      name,
      url,
      artist,
      credit,
      attributionRequired,
      source,
    };
  }

  private static resolveWikipedia(
    response: AnimaliaListItemApiResponse,
  ): { url: string } | null {
    if (response.wikipedia?.url?.trim()) {
      return {
        url: response.wikipedia.url,
      };
    }

    if (typeof response.wikipediaUrl === 'string' && response.wikipediaUrl.trim() !== '') {
      return {
        url: response.wikipediaUrl,
      };
    }

    return null;
  }

  private static taxonomyFromApiResponse(
    response: AnimaliaListingTaxonomyApiResponse | null | undefined,
  ): AnimaliaListingTaxonomy | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const key = typeof response.key === 'string' && isAnimaliaTaxonomyFilterKey(response.key)
      ? response.key
      : null;
    const value = AnimaliaListingMapper.resolveText([response.value]);

    if (!key || !value) {
      return null;
    }

    const navigableTaxonomy = createAnimaliaNavigableTaxonomy(key, value);

    if (!navigableTaxonomy) {
      return null;
    }

    const title = AnimaliaListingMapper.resolveText([response.title]) ?? value;
    const summary = AnimaliaListingMapper.resolveText([response.summary]);
    const description = AnimaliaListingMapper.resolveText([response.description]);
    const image = AnimaliaListingMapper.resolveImage(response.image, response.imageLicense, title);
    const sections = AnimaliaListingMapper.resolveTaxonomySections(response.sections);
    const wikipediaUrl = AnimaliaListingMapper.resolveText([response.wikipedia?.url, response.wikipediaUrl]);

    return new AnimaliaListingTaxonomy(
      navigableTaxonomy.filterKey,
      navigableTaxonomy.value,
      title,
      summary,
      description,
      image,
      sections,
      wikipediaUrl,
      response.completed === true,
    );
  }

  private static toTaxonomyLandingDTO(
    taxonomy: AnimaliaListingTaxonomy | null,
  ): AnimaliaListingTaxonomyLandingDTO | null {
    if (!taxonomy) {
      return null;
    }

    return {
      key: taxonomy.key,
      label: TAXONOMY_LABELS[taxonomy.key],
      value: taxonomy.value,
      title: taxonomy.title,
      summary: taxonomy.summary,
      description: taxonomy.description,
      imageSrc: taxonomy.image?.src ?? null,
      imageAlt: taxonomy.image?.alt ?? taxonomy.title,
      imageLicenseName: taxonomy.image?.license?.name ?? null,
      imageLicenseUrl: taxonomy.image?.license?.url ?? null,
      imageArtist: taxonomy.image?.license?.artist ?? null,
      imageCredit: taxonomy.image?.license?.credit ?? null,
      imageAttributionRequired: taxonomy.image?.license?.attributionRequired ?? false,
      imageSource: taxonomy.image?.license?.source ?? null,
      wikipediaUrl: taxonomy.wikipediaUrl,
      sections: taxonomy.sections.map((section) => ({
        title: section.title,
        body: section.body,
      })),
      completed: taxonomy.completed,
      canonicalUrl: buildAnimaliaTaxonomyCanonicalRoute(taxonomy.key, taxonomy.value),
    };
  }

  private static resolveTaxonomySections(
    sections: ReadonlyArray<AnimaliaSectionApiResponse> | undefined,
  ): ReadonlyArray<AnimaliaListingTaxonomySection> {
    if (!sections) {
      return [];
    }

    return sections.reduce<AnimaliaListingTaxonomySection[]>((accumulator, section) => {
      const body = AnimaliaListingMapper.resolveText([section.content]);

      if (!body) {
        return accumulator;
      }

      accumulator.push({
        title: AnimaliaListingMapper.resolveText([section.title]) ?? 'Contexto editorial',
        body,
      });

      return accumulator;
    }, []);
  }

  private static resolveNarrativeBlocks(
    response: AnimaliaListItemApiResponse,
  ): ReadonlyArray<AnimaliaNarrativeBlock> {
    const narrativeBlocks = response.narrativeBlocks ?? response.sections?.map((section) => ({
      title: section.title,
      body: section.content,
    })) ?? [];

    return narrativeBlocks
      .filter((block) => typeof block.body === 'string' && block.body.trim() !== '')
      .map(
        (block) =>
          new AnimaliaNarrativeBlock(
            block.title?.trim() || 'Natural history',
            block.body?.trim() || '',
          ),
      );
  }

  private static resolveTaxonomy(
    response: AnimaliaListItemApiResponse,
    scientificName: string,
    family: string | null,
  ): ReadonlyArray<AnimaliaTaxonomyEntry> {
    const genus = AnimaliaListingMapper.resolveText([
      response.taxonomy?.genus,
      response.genus,
      scientificName.trim().split(/\s+/)[0],
    ]);
    const species = AnimaliaListingMapper.resolveText([
      response.taxonomy?.species,
      response.species,
      scientificName,
    ]);
    const levels: Array<[AnimaliaTaxonomyFilterKey, string | null]> = [
      ['kingdom', AnimaliaListingMapper.resolveText([response.taxonomy?.kingdom, response.kingdom])],
      ['phylum', AnimaliaListingMapper.resolveText([response.taxonomy?.phylum, response.phylum])],
      [
        'class',
        AnimaliaListingMapper.resolveText([
          response.taxonomy?.class,
          response.taxonomy?.className,
          response.class,
          response.className,
        ]),
      ],
      ['order', AnimaliaListingMapper.resolveText([response.taxonomy?.order, response.order])],
      ['family', family],
      ['genus', genus],
      ['species', species],
    ];

    return levels.reduce<AnimaliaTaxonomyEntry[]>((entries, [level, value]) => {
      if (value === null) {
        return entries;
      }

      entries.push(new AnimaliaTaxonomyEntry(level, value));
      return entries;
    }, []);
  }
}
